import type {
  CheckItem,
  DraftBrief,
  DraftState,
  ReplyDraft,
  ReplyDraftSummary,
  Segment,
  SourcePublication,
} from '../model/types';

import type { components, paths } from '@primum/api-types';

type Schemas = components['schemas'];

export type WireReplyDraft = Schemas['ReplyDraft'];

export type WireReplyDraftSummary = Schemas['ReplyDraftSummary'];

type WireState = NonNullable<Schemas['ReplyDraftState']>;

type WireBrief = NonNullable<Schemas['ReplyDraftBrief']>;

type WireCard = NonNullable<Schemas['ReplyDraftCard']>;

type WirePublication = NonNullable<
  paths['/v2/publications/{id}']['get']['responses'][200]['content']['application/json']['data']
>;

function toSegment(segment: Schemas['ReplyDraftSegment']): Segment {
  return {
    id: segment.id,
    kind: segment.kind,
    origin: segment.origin,
    text: segment.text,
    struck: segment.struck,
    included: segment.included,
    swap: segment.swap,
    flag: segment.flag,
    sourceLabel: segment.sourceLabel,
    sourceUrl: segment.sourceUrl,
    publicationId: segment.publicationId,
    chipId: segment.chipId,
    severity: segment.severity,
  } satisfies Segment;
}

export function toCheckItem(item: Schemas['ReplyDraftCheckItem']): CheckItem {
  return { segmentId: item.segmentId, label: item.label, severity: item.severity };
}

function toDraftState(state: WireState): DraftState {
  return {
    optionKey: state.optionKey,
    segments: state.segments.map(toSegment),
    composedText: state.composedText,
    pendingMustFix: state.pendingMustFix.map(toCheckItem),
  };
}

function toBrief(brief: WireBrief): DraftBrief {
  return {
    caseFacts: brief.caseFacts,
    history: brief.history?.trim() || null,
    historyCaseCount: brief.historyCaseCount ?? null,
    changedRecently: brief.changedRecently.map((item) => ({
      text: item.text,
      date: item.date ?? null,
      source: item.source ?? null,
      url: item.url,
      publicationId: item.publicationId,
    })),
    peerSummary: brief.peerSummary ?? null,
  };
}

/**
 * The card's main question only. The card can carry a second question, but
 * the API has no way to record an answer to it, so the app never shows it.
 */
function toCard(card: WireCard) {
  return { question: card.main.question, options: card.main.options };
}

export function toReplyDraft(draft: WireReplyDraft): ReplyDraft {
  return {
    id: draft.id,
    inquiryId: draft.inquiryId,
    conversationId: draft.conversationId,
    status: draft.status,
    stateVersion: draft.stateVersion,
    askerFollowedUpAt: draft.askerFollowedUpAt,
    card: draft.card ? toCard(draft.card) : null,
    brief: draft.brief ? toBrief(draft.brief) : null,
    draft: draft.draft ? toDraftState(draft.draft) : null,
  };
}

export function toReplyDraftSummary(summary: WireReplyDraftSummary): ReplyDraftSummary {
  return {
    id: summary.id,
    inquiryId: summary.inquiryId,
    conversationId: summary.conversationId,
    status: summary.status,
    mainQuestion: summary.mainQuestion,
    readyAt: summary.readyAt,
  } satisfies ReplyDraftSummary;
}

/** A catalog record, with only the summary fields the details show; the summary's tags are not drawn. */
export function toSourcePublication(publication: WirePublication): SourcePublication {
  const { summary } = publication;

  return {
    publicationId: publication.publicationId,
    kind: publication.kind,
    title: publication.title,
    authors: publication.authors,
    citation: publication.citation,
    journal: publication.journal,
    journalAbbr: publication.journalAbbr,
    year: publication.year,
    volume: publication.volume,
    issue: publication.issue,
    pages: publication.pages,
    doi: publication.doi,
    pmid: publication.pmid,
    pmcid: publication.pmcid,
    nctId: publication.nctId,
    url: publication.url,
    summary: summary
      ? {
          whyItMatters: summary.whyItMatters,
          headlineResult: summary.headlineResult,
          design: summary.design,
          population: summary.population,
          intervention: summary.intervention,
          safety: summary.safety,
          authorsConclusion: summary.authorsConclusion,
          limitations: summary.limitations,
        }
      : null,
  };
}
